import React, {FC} from 'react';
import {Alert, FileInputField} from '@deere/fuel-react';
import {Button, Card, Container, Grid, LinearProgress} from '@mui/material';

import './bulkClassLoad.css';
import {useOktaAuth} from '@okta/okta-react';
import {useTranslation} from 'react-i18next';
import BulkPaginatedTable from '../components/BulkPaginatedTable';

const MAX_ALERT_DISPLAY_TIME = 5000;

export const BulkClassLoad: FC<unknown> = () => {
    const {authState} = useOktaAuth();
    const {t} = useTranslation();
    const [state, setState] = React.useState({
        alertTitle: '',
        alertVariant: 'info',
        alertLoader: false,
        updateTableData: false,
        API_CALL_DELAY_MS: 60000
    });
    const handleTemplateButtonClick = (): void => {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/bulkloadtemplate`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/octet-stream'
            }
        })
            .then((response) => {
                return response.blob();
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = 'Bulk Class Load Template.xlsx';
                link.click();
                URL.revokeObjectURL(url);
                return null;
            })
            .catch((error) => {
                console.error('Error fetching the template:', error);
            });
    };

    const handleGuideLinkClick = (e: any): void => {
        e.stopPropagation();
        e.preventDefault();
        fetch(`${process.env.REACT_APP_BACKEND_URL}/bulkloadguide`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'Content-Type': 'application/octet-stream'
            }
        })
            .then((response) => {
                return response.blob();
            })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');

                link.href = url;
                link.download = 'Bulk Class Load Guide.docx';
                link.click();
                URL.revokeObjectURL(url);
                return null;
            })
            .catch((error) => {
                console.error('Error fetching the guide:', error);
            });
    };

    const onDropAccepted = (file: any): void => {
        const formData = new FormData();

        setState((prevState) => ({
            ...prevState,
            alertTitle: t('bulkClassLoad.uploading'),
            alertVariant: 'info',
            alertLoader: true
        }));
        formData.append('file', file[0]);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/classfileupload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${authState?.accessToken?.accessToken}`,
                'idToken': `Bearer ${authState?.idToken?.idToken}`
            },
            body: formData
        })
            .then((resp) => {
                if (!resp.ok) {
                    setState((prevState) => ({
                        ...prevState,
                        alertVariant: 'error'
                    }));
                }
                return resp.json();
            })
            .then((data) => {
                setState((prevState) => ({
                    ...prevState,
                    alertTitle: data.message,
                    alertLoader: false
                }));
                setTimeout(() => {
                    setState((prevState) => ({
                        ...prevState,
                        alertTitle: ''
                    }));
                },
                MAX_ALERT_DISPLAY_TIME);
                setTimeout((): void => {
                    // updating the result table after 25 sec
                    setState((prevState) => ({
                        ...prevState,
                        updateTableData: !prevState.updateTableData
                    }));
                }, state.API_CALL_DELAY_MS);
                return data;
            })
            .catch((e) => {
                setState((prevState) => ({
                    ...prevState,
                    alertTitle: t('bulkClassLoad.failed'),
                    alertVariant: 'error',
                    alertLoader: false
                }));
                setTimeout(() => {
                    setState((prevState) => ({
                        ...prevState,
                        alertTitle: ''
                    }));
                },
                MAX_ALERT_DISPLAY_TIME);
                console.error('Error uploading file: ', e);
            });
        return file;
    };

    const fileLoader = state.alertLoader ? <LinearProgress value={25}/> : <></>;

    const disableFileUpload = (): boolean => {
        if (state.alertLoader) {
            return true;
        }
        return false;
    };

    const alertComponent = (): JSX.Element => {
        const content = (<>
            <Alert
                hasCloseButton={true}
                onClose={!state.alertLoader ? (): void => {
                    setState((prevState) => ({
                        ...prevState,
                        alertTitle: ''
                    }));
                } : (): void => { }}
                title={state.alertTitle}
                variant={state.alertVariant === 'error' ? 'error' : 'info'}
            >
                {fileLoader}
            </Alert><br/></>
        );

        return content;
    };

    return (
        <Container
            sx={{
                paddingTop: '1rem',
                textAlign: 'center'
            }}
        >
            {state.alertTitle !== '' ? alertComponent() : <></>}
            <Card>
                <Grid
                    alignItems='center'
                    container
                    justifyContent='center'
                    spacing={2}
                >
                    <Grid
                        item
                        xs={6}
                    >
                        <Button
                            onClick={handleTemplateButtonClick}
                            size='medium'
                            style={{
                                marginLeft: '0.4rem'
                            }}
                            variant='primary'
                        >
                            {t('bulkClassLoad.sampleTemplate')}
                        </Button>
                        <br/><br/>
                        <span
                            style={{
                                marginLeft: '0.6rem',
                                fontStyle: 'italic',
                                fontSize: '0.8rem'
                            }}
                        >
                            {t('bulkClassLoad.guideText')}
                            <a
                                href='#'
                                onClick={handleGuideLinkClick}
                                style={{
                                    marginLeft: '0.2rem'
                                }}
                            >
                                {t('bulkClassLoad.guideLink')}
                            </a>
                        </span>
                    </Grid>
                    <Grid
                        item
                        xs={6}
                    >
                        <FileInputField
                            accept={{
                                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
                            }}
                            disabled={disableFileUpload()}
                            label=''
                            maxFiles={1}
                            maxSize={10485760}
                            minSize={1024}
                            onDropAccepted={(file): void => onDropAccepted(file)}
                            showHelperText
                            textMap={{
                                actionChoose: t('bulkClassLoad.chooseFile'),
                                actionAdd: t('bulkClassLoad.chooseFile'),
                                actionClear: t('bulkClassLoad.clearFile')
                            }}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <BulkPaginatedTable
                            updateTableData={state.updateTableData}
                        />
                    </Grid>
                </Grid>
            </Card>
        </Container>
    );
};
